import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import {
  Users, Trophy, ChartBar, Eye, Brain, ChatCircle,
  Certificate, Path, SpinnerGap, Crown, ShieldCheck, UserCircle,
  ArrowUp, ArrowDown, Minus
} from "@phosphor-icons/react";

import { API_BASE as API } from "../lib/apiBase";
import { getCanonicalLabel, roleLabelsFor } from "../lib/canonicalRoles";
import { useLang } from "../contexts/LanguageContext";

// Vincent 2026-06-01 — Source unique (Gestion des rôles). job_title custom prioritaire,
// fallback sur le rôle canonique (avec libellés personnalisés via user.role_labels).
const ROLE_ICONS = { founder: Crown, president: Crown, ceo: Crown, vp: ShieldCheck, admin: ShieldCheck, concepteur: ShieldCheck, employee_advanced: UserCircle, employee_trained: UserCircle, employee_new: UserCircle, employee: UserCircle, viewer: UserCircle };

export default function TeamAnalyticsPage() {
  // Tâche 1387 — les libellés de rôles suivent la langue de l'écran.
  const { tr, lang } = useLang();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("points");

  useEffect(() => {
    (async () => {
      try {
        const { data: res } = await axios.get(`${API}/api/organizations/analytics`, { withCredentials: true });
        setData(res);
      } catch {}
      setLoading(false);
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <SpinnerGap size={32} className="animate-spin text-blue-500" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-16">
        <Users size={48} className="mx-auto mb-4 text-zinc-400" />
        <h2 className="text-xl font-bold mb-2">{tr("Analytique indisponible", "Analytics unavailable")}</h2>
        <p className="text-zinc-500">{tr("Vous devez être associé à une organisation.", "You must be attached to an organisation.")}</p>
      </div>
    );
  }

  const { members, totals } = data;
  const sorted = [...members].sort((a, b) => {
    if (sortBy === "points") return b.stats.points - a.stats.points;
    if (sortBy === "quiz") return b.stats.avg_quiz_score - a.stats.avg_quiz_score;
    if (sortBy === "views") return b.stats.video_views - a.stats.video_views;
    if (sortBy === "name") return a.name.localeCompare(b.name);
    return 0;
  });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="team-analytics-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold font-['Outfit']">{tr("Analytique Équipe", "Team analytics")}</h1>
        <p className="text-sm text-zinc-500 mt-1">{tr("Suivi de progression des membres de votre organisation", "Progress tracking for the members of your organisation")}</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl p-4" data-testid="stat-members">
          <Users size={20} className="text-blue-500 mb-2" />
          <div className="text-2xl font-bold">{totals.member_count}</div>
          <div className="text-xs text-zinc-500">{tr("Membres", "Members")}</div>
        </div>
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl p-4" data-testid="stat-videos">
          <Eye size={20} className="text-purple-500 mb-2" />
          <div className="text-2xl font-bold">{totals.video_count}</div>
          <div className="text-xs text-zinc-500">{tr("Vidéos", "Videos")}</div>
        </div>
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl p-4" data-testid="stat-knowledge">
          <Brain size={20} className="text-teal-500 mb-2" />
          <div className="text-2xl font-bold">{totals.knowledge_count}</div>
          <div className="text-xs text-zinc-500">{tr("Connaissances", "Knowledge")}</div>
        </div>
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl p-4" data-testid="stat-avg-score">
          <Trophy size={20} className="text-amber-500 mb-2" />
          <div className="text-2xl font-bold">
            {members.length > 0 ? Math.round(members.reduce((sum, m) => sum + m.stats.avg_quiz_score, 0) / members.length) : 0}%
          </div>
          <div className="text-xs text-zinc-500">{tr("Score moyen aux quiz", "Average quiz score")}</div>
        </div>
      </div>

      {/* Sort controls */}
      <div className="flex gap-2">
        {[
          { key: "points", label: "Points" },
          { key: "quiz", label: "Quiz" },
          { key: "views", label: tr("Vidéos vues", "Videos watched") },
          { key: "name", label: tr("Nom", "Name") },
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setSortBy(key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${sortBy === key ? "bg-blue-600 text-white" : "bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 hover:bg-zinc-200 dark:hover:bg-zinc-700"}`}
            data-testid={`sort-${key}`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Members Table - Desktop */}
      <div className="hidden md:block bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl overflow-hidden" data-testid="members-table">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-200 dark:border-zinc-800 text-xs text-zinc-500 uppercase tracking-wider">
                <th className="text-left px-4 py-3">#</th>
                <th className="text-left px-4 py-3">{tr("Membre", "Member")}</th>
                <th className="text-left px-4 py-3">{tr("Rôle", "Role")}</th>
                <th className="text-center px-4 py-3">{tr("Vidéos vues", "Videos watched")}</th>
                <th className="text-center px-4 py-3">Quiz</th>
                <th className="text-center px-4 py-3">{tr("Score moy.", "Avg. score")}</th>
                <th className="text-center px-4 py-3">{tr("Clavardage", "Chat")}</th>
                <th className="text-center px-4 py-3">Points</th>
                <th className="text-center px-4 py-3">{tr("Niveau", "Level")}</th>
                <th className="text-center px-4 py-3">{tr("Certifs", "Certs")}</th>
                <th className="text-center px-4 py-3">{tr("Initiation", "Onboarding")}</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((member, idx) => {
                const RoleIcon = ROLE_ICONS[member.org_role] || UserCircle;
                const { stats } = member;
                return (
                  <tr key={member.id} className="border-b border-zinc-100 dark:border-zinc-800/50 hover:bg-zinc-50 dark:hover:bg-zinc-800/30 transition-colors" data-testid={`analytics-row-${member.id}`}>
                    <td className="px-4 py-3">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${idx < 3 ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" : "bg-zinc-100 dark:bg-zinc-800 text-zinc-500"}`}>
                        {idx + 1}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-sm">{member.name}</div>
                      <div className="text-xs text-zinc-500">{member.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="flex items-center gap-1.5 text-xs">
                        <RoleIcon size={12} />
                        {member.job_title || getCanonicalLabel(member.org_role, roleLabelsFor(user, lang), member.is_founder, lang)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-sm font-medium">{stats.video_views}</td>
                    <td className="px-4 py-3 text-center text-sm font-medium">{stats.quiz_count}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-sm font-bold ${stats.avg_quiz_score >= 80 ? "text-teal-500" : stats.avg_quiz_score >= 50 ? "text-amber-500" : "text-zinc-400"}`}>
                        {stats.quiz_count > 0 ? `${stats.avg_quiz_score}%` : "-"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-sm font-medium">{stats.chat_queries}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-sm font-bold text-blue-500">{stats.points}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-xs font-bold px-2 py-0.5 rounded-full">
                        {tr("Nv.", "Lvl ")}{stats.level}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-sm font-medium">{stats.certificates}</td>
                    <td className="px-4 py-3 text-center">
                      {stats.onboarding_total > 0 ? (
                        <span className={`text-xs font-medium ${stats.onboarding_complete === stats.onboarding_total ? "text-teal-500" : "text-amber-500"}`}>
                          {stats.onboarding_complete}/{stats.onboarding_total}
                        </span>
                      ) : (
                        <span className="text-xs text-zinc-400">-</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {sorted.length === 0 && (
          <div className="p-8 text-center text-zinc-400">
            <Users size={36} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm">{tr("Aucun membre dans l'organisation", "No member in the organisation")}</p>
          </div>
        )}
      </div>

      {/* Members Cards - Mobile */}
      <div className="md:hidden space-y-3" data-testid="members-mobile">
        {sorted.map((member, idx) => {
          const RoleIcon = ROLE_ICONS[member.org_role] || UserCircle;
          const { stats } = member;
          return (
            <div key={member.id} className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl p-4" data-testid={`mobile-member-${member.id}`}>
              <div className="flex items-center gap-3 mb-3">
                <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${idx < 3 ? "bg-amber-100 text-amber-700" : "bg-zinc-100 text-zinc-500"}`}>{idx + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{member.name}</div>
                  <div className="text-xs text-zinc-500 truncate">{member.email}</div>
                </div>
                <span className="flex items-center gap-1 text-xs bg-zinc-100 dark:bg-zinc-800 px-2 py-1 rounded-full">
                  <RoleIcon size={12} /> {member.job_title || getCanonicalLabel(member.org_role, roleLabelsFor(user, lang), member.is_founder, lang)}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-zinc-50 dark:bg-zinc-800/50 rounded-lg py-2">
                  <div className="text-sm font-bold">{stats.video_views}</div>
                  <div className="text-[10px] text-zinc-500">{tr("Vidéos", "Videos")}</div>
                </div>
                <div className="bg-zinc-50 dark:bg-zinc-800/50 rounded-lg py-2">
                  <div className="text-sm font-bold">{stats.quiz_count > 0 ? `${stats.avg_quiz_score}%` : "-"}</div>
                  <div className="text-[10px] text-zinc-500">Quiz</div>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg py-2">
                  <div className="text-sm font-bold text-blue-600">{stats.points} pts</div>
                  <div className="text-[10px] text-zinc-500">{tr("Nv.", "Lvl ")}{stats.level}</div>
                </div>
              </div>
            </div>
          );
        })}
        {sorted.length === 0 && (
          <div className="p-8 text-center text-zinc-400">
            <Users size={36} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm">{tr("Aucun membre dans l'organisation", "No member in the organisation")}</p>
          </div>
        )}
      </div>
    </div>
  );
}
